import {
	BrokerContactCreationSubRole,
	ContactCreationRoles,
	DuneContactCreationSubRole,
} from "@dune/types";
import {
	getErrorMessage,
	getStandardErrorData,
	hasAccessToPlatform,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { updateContact } from "../../api/mutations/contacts.mutations";
import { useGetContactDetails } from "../../api/queries/contacts.queries";
import { contactsQueryKeys } from "../../api/query-keys/contacts.query-keys";
import { UpdateContactBody } from "../../api/types/contacts/contacts.types";
import {
	ContactFormData,
	emptyContactDefaultValues,
	zAddContactFormSchema,
} from "./useAddContactForm";

export const useUpdateContactForm = (
	contactToUpdateId: number,
	onSuccess: () => void,
) => {
	const formMethods = useForm<ContactFormData>({
		defaultValues: emptyContactDefaultValues,
		resolver: zodResolver(zAddContactFormSchema),
	});

	const { reset } = formMethods;

	const { contact, isLoading: isLoadingContact } =
		useGetContactDetails(contactToUpdateId);

	const queryClient = useQueryClient();

	const { mutate: updateContactMutate, isPending: isPendingUpdate } =
		useMutation({
			mutationFn: (updateContactBody: UpdateContactBody) =>
				updateContact(contactToUpdateId, updateContactBody),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: contactsQueryKeys.all,
				});
				onSuccess?.();
			},
			onError: (error) => {
				const errorData = getStandardErrorData(error);
				if (errorData?.statusCode === 409) {
					toast.error("Cette adresse email est déjà utilisée");
					return;
				}
				const errorMessage = getErrorMessage(error);
				toast.error(errorMessage);
			},
		});

	const onSubmit = () =>
		formMethods.handleSubmit((data) => {
			const body: UpdateContactBody = {
				email:
					!data.giveAccessToPlatform && data.email === ""
						? undefined
						: data.email,
				firstname: data.firstname,
				lastname: data.lastname,
			};
			if (data.type === "BROKER") {
				body.subRole = data.brokerSubRole ?? undefined;
			}
			if (data.type === "DUNE") {
				body.subRole = data.duneSubRole ?? undefined;
			}
			updateContactMutate(body);
		});

	useEffect(() => {
		if (contact) {
			reset({
				type: contact.role as ContactCreationRoles,
				email: contact.email ?? "",
				firstname: contact.firstname,
				lastname: contact.lastname,
				giveAccessToPlatform: hasAccessToPlatform(contact),
				brokerSocietyId: contact.society?.id.toString() ?? "",
				brokerSubRole:
					contact.role === "BROKER"
						? (contact.subrole as BrokerContactCreationSubRole)
						: null,
				syndicSocietyId: contact.society?.id.toString() ?? "",
				duneSubRole:
					contact.role === "DUNE"
						? (contact.subrole as DuneContactCreationSubRole)
						: null,
				insurerSocietyId: contact.society?.id.toString() ?? "",
				tpaSocietyId: contact.society?.id.toString() ?? "",
			});
		}
	}, [contact, reset]);

	return {
		onSubmit,
		isLoadingContact,
		isPendingUpdate,
		formMethods,
	};
};
